import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useBusinessLink, useStores } from '~/hooks';
import {
    get,
    getAddonActionText,
    getErrorMsg,
    getVipErrMsg,
    isEmpty,
    isNumber,
    isString,
    isUndefined,
    localStorageManager,
    openInNewTab,
    removeDom,
    StorageKeyManager,
    tracker
} from '~/utils';
import Intl from '@fx-ui/intl';
import { TRACKER } from '~/constants';
import { Alert } from '@fx-ui/fine-design';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import { getVersionInfoUrl } from '@fx-ui/platform-sdk';

export interface IUpgradeBufferState {
    /**
     * number: 后端错误码
     * symbol：纯前端错误唯一标识
     */
    code: number | string | symbol;
    expireDays?: number;
}

interface IUpgradeTipProps {
    visible: boolean;
    title?: string;
    okBtnConfig?: {
        text?: string | null;
        cancelText?: string | null;
        onClick?: () => void;
    };
    onClose: () => void;
    bufferState?: IUpgradeBufferState;
    useCookie?: boolean; // 是否记录 cookie，用于控制仅提示一次
    hasTip?: boolean; // 是否显示左侧 footer tip
}

/**
 * 高级功能不可用：升级/增购 Alert 提示
 *
 * @param props
 */
const UpgradeTip: React.FC<IUpgradeTipProps> = (props) => {
    const { visible, onClose, okBtnConfig, bufferState, useCookie = false, hasTip = true } = props;
    const { corpUserStore } = useStores();
    const { purchaseLink } = useBusinessLink();
    const [show, setShow] = useState(false);

    const hasSysAdmin = corpUserStore.hasSysAdmin;
    const errorCode = bufferState?.code;
    const expireDays = bufferState?.expireDays;

    useEffect(() => {
        if (visible) {
            if (!errorCode) {
                setShow(false);
                return;
            }
            const upgradeTipKey = StorageKeyManager.getUpgradeTipKey({
                username: corpUserStore.username
            });
            if (useCookie && corpUserStore.isUserExist) {
                if (localStorageManager.get(upgradeTipKey)) {
                    setShow(false);
                    return;
                } else {
                    localStorageManager.set(upgradeTipKey, 'true', 1);
                }
            }
            tracker(TRACKER.VIP_LIMIT_ALERT, {
                p1: String(errorCode),
                p2: String(expireDays)
            });
            setShow(true);
        } else {
            setShow(false);
        }
    }, [visible]);

    if (!errorCode) {
        return null;
    }

    const errInfo = getVipErrMsg({
        corpUserStore,
        code: errorCode
    });

    const getDefaultErrorMsg = () => {
        return (
            ((isNumber(errorCode) || isString(errorCode)) && getErrorMsg(bufferState)) ||
            get(bufferState, 'message', '')
        );
    };

    const getUpgradeInfo = () => {
        const { title, msg, memMsg } = errInfo;
        const message = !hasSysAdmin && memMsg ? memMsg : msg;

        // 优先使用自定义标题
        if (props.title) {
            return {
                title: props.title,
                message
            };
        }

        if (isEmpty(errInfo)) {
            return {
                title: getDefaultErrorMsg()
            };
        }

        if (!title) {
            // 没有title，则将消息作为title
            return {
                title: message
            };
        }
        return {
            title,
            message
        };
    };

    // 跳转到高级功能所在帮助文档链接
    const renderFuncIntro = (docLink: string) => {
        return (
            <Hyperlink
                compact
                underline={false}
                target="_self"
                onClick={() => {
                    setShow(false);
                    onClose();
                    // 用href会存在先触发click导致a标签重新渲染，href改变，改变后跳转的地址也变为新的href的情况，故修改为openInNewTab
                    openInNewTab(docLink);
                }}
            >
                {Intl.get('vip.advancedFeatures')}
            </Hyperlink>
        );
    };

    const getUpgradeTip = () => {
        const { doc } = errInfo;
        if (doc) {
            return renderFuncIntro(doc);
        } else {
            // 跳转到 企业管理-版本信息-系统容量
            return (
                <Hyperlink
                    compact
                    underline={false}
                    target="_self"
                    onClick={() => {
                        tracker(TRACKER.USAGE_LIMIT_WARNING_DETAILS_CLICK);
                        setShow(false);
                        onClose();
                        // 原因同上
                        openInNewTab(getVersionInfoUrl(corpUserStore.tenantId));
                    }}
                >
                    {Intl.get('vip.usedFeatures')}
                </Hyperlink>
            );
        }
    };

    if (hasSysAdmin) {
        const { text, onClick, cancelText } = okBtnConfig || {};
        const { allowAddon, isAddonUsage } = errInfo;

        const defaultCancelText = allowAddon
            ? Intl.get('vip.purchase.no')
            : Intl.get('vip.upgrade.no');
        const okText = getAddonActionText(allowAddon, isAddonUsage);

        return (
            <>
                <Alert
                    visible={show}
                    onOk={() => {
                        if (onClick) {
                            onClick();
                        } else {
                            openInNewTab(purchaseLink, '_blank');
                        }
                        setShow(false);
                        onClose();
                    }}
                    text4Ok={isUndefined(text) ? okText : text}
                    onCancel={() => {
                        onClose();
                    }}
                    text4Cancel={isUndefined(cancelText) ? defaultCancelText : cancelText}
                    {...getUpgradeInfo()}
                    footerLeft={hasTip && getUpgradeTip()}
                />
            </>
        );
    }

    return (
        <Alert
            visible={show}
            onOk={onClose}
            {...getUpgradeInfo()}
            text4Ok={Intl.get('iSee')}
            text4Cancel={null}
            footerLeft={hasTip && errInfo.doc && renderFuncIntro(errInfo.doc)}
        />
    );
};

export default UpgradeTip;

/**
 * 命令式打开 UpgradeTip 组件
 * @param config
 */
export const openUpgradeTip = (
    config: Omit<IUpgradeTipProps, 'visible' | 'onClose'> & { onClose?: () => void }
) => {
    const $el = document.createElement('div');
    document.body.appendChild($el);
    const root = createRoot($el);

    const handleClose = () => {
        root.unmount();
        removeDom($el);
        config.onClose && config.onClose();
    };

    root.render(<UpgradeTip visible {...config} onClose={handleClose} />);
};
